import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, Users, TrendingUp, Calendar } from 'lucide-react'
import { Card, CardContent } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import LazyImage from '../ui/LazyImage'
import leaderboardService from '../../services/leaderboardService'

const ProjectRankingCard = ({ project, type, index }) => {
  const navigate = useNavigate()

  const handleViewProject = () => {
    navigate(`/projects/${project.slug}`)
  }

  const statusBadge = leaderboardService.getStatusBadge(project.status)

  // Minimal consistent rank styling
  const rankDisplay = {
    text: `#${project.rank}`,
    bg: 'bg-neutral-100',
    textColor: 'text-neutral-700'
  }

  // Primary metric based on leaderboard type
  const getPrimaryMetric = () => {
    switch (type) {
      case 'funding':
        return { label: 'Raised', value: project.funding_display }
      case 'supporters':
        return { label: 'Supporters', value: project.supporter_count }
      case 'trending':
        return { label: 'Recent', value: `${project.recent_donations || 0} donations` }
      case 'success':
        return { label: 'Goal', value: project.progress_display }
      default:
        return { label: 'Raised', value: project.funding_display }
    }
  }

  const primaryMetric = getPrimaryMetric()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{ y: -2 }}
      className="group cursor-pointer"
      onClick={handleViewProject}
    >
      <Card className="border border-neutral-200 hover:border-neutral-300 hover:shadow-md transition-all duration-200 bg-white w-full">
        <CardContent className="p-3 sm:p-4">
          {/* Mobile-responsive layout */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            {/* Top row on mobile: Rank, Image, Title */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Rank */}
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${rankDisplay.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <span className={`text-sm sm:text-lg font-semibold ${rankDisplay.textColor}`}>
                  {rankDisplay.text}
                </span>
              </div>

              {/* Project image */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0">
                {project.cover_image ? (
                  <LazyImage
                    src={project.cover_image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                        <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-neutral-400" />
                      </div>
                    }
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                    <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-neutral-400" />
                  </div>
                )}
              </div>

              {/* Project info - takes remaining space */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-neutral-900 line-clamp-1 mb-1 group-hover:text-primary-teal transition-colors text-sm sm:text-base">
                  {project.title}
                </h3>
                
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={`${statusBadge.color} text-xs`}>
                    {statusBadge.text}
                  </Badge>
                  <span className="text-xs sm:text-sm text-neutral-500 truncate">
                    {project.owner_display}
                  </span>
                </div>

                {/* Compact stats - mobile friendly */}
                <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-600">
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{project.supporter_count}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{project.created_display}</span>
                    <span className="sm:hidden">{new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Primary metric - right side on desktop, below on mobile */}
            <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center text-right flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-neutral-100">
              <div className="sm:mb-1">
                <div className="text-lg sm:text-xl font-bold text-neutral-900">
                  {primaryMetric.value}
                </div>
                <div className="text-xs sm:text-sm text-neutral-500">
                  {primaryMetric.label}
                </div>
              </div>
              
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleViewProject()
                }}
                size="sm"
                className="bg-primary-teal hover:bg-primary-teal/90 text-white px-3 sm:px-4 text-xs sm:text-sm"
              >
                <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                View
              </Button>
            </div>
          </div>

          {/* Simple progress bar for active projects - full width */}
          {project.status === 'active' && project.funding_goal && parseFloat(project.funding_goal) > 0 && (
            <div className="mt-3 pt-3 border-t border-neutral-100">
              <div className="flex justify-between text-xs sm:text-sm text-neutral-600 mb-2">
                <span>Progress</span>
                <span className="font-medium">{project.progress_display}</span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-2">
                <div
                  className="bg-primary-teal h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, project.progress_percentage || 0)}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default ProjectRankingCard